import { useState, useRef, useCallback, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  ChevronDown,
  ChevronRight,
  Calculator,
  Loader2,
  RefreshCw,
  Download,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  X,
  Filter,
  Coins,
} from 'lucide-react'
import { cn, downloadJSON } from '@/lib/utils'
import { lcaAPI } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'
import { toast } from 'sonner'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'
import type { TaskPendingError } from '@/lib/types'

interface ImpactDetailViewProps {
  activity: ActivityWithExchanges | null
  projectName?: string
  projectDatabase?: string
}

interface MethodInfo {
  method_specification: string
  category: string
  subcategory: string
  indicator: string
  unit: string
  description: string
}

interface LCAResult {
  lca_score: { value: number; unit: string }
  method_specification: string
  calculation_time?: number
  functional_unit?: { quantity: number; unit: string; activity_name: string }
  database?: string
  activity_details?: Record<string, Record<string, string>>
}

type CalcStatus = 'idle' | 'searching' | 'ready' | 'submitting' | 'polling' | 'done' | 'error'

// Well-known method families for quick filter buttons
const POPULAR_CATEGORIES = [
  'IPCC 2021',
  'ReCiPe 2016 v1.03, midpoint (H)',
  'EF v3.1',
  'CML v4.8 2016',
  'TRACI v2.1',
]

export function ImpactDetailView({
  activity,
  projectName,
  projectDatabase,
}: ImpactDetailViewProps) {
  const [status, setStatus] = useState<CalcStatus>('idle')
  const [methodQuery, setMethodQuery] = useState('')
  const [methods, setMethods] = useState<MethodInfo[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<MethodInfo | null>(null)
  const [result, setResult] = useState<LCAResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [methodsOpen, setMethodsOpen] = useState(true)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup poll timer
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  // Load categories when database changes
  useEffect(() => {
    if (!projectDatabase) return
    lcaAPI
      .getMethodCategories(projectDatabase)
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => {})
  }, [projectDatabase])

  const doSearch = useCallback(
    async (keywords: string, category?: string | null) => {
      if (!keywords.trim() || !projectDatabase) return
      setStatus('searching')
      setErrorMsg(null)
      try {
        const params: { keywords: string; database: string; category?: string; limit: number } = {
          keywords: keywords.trim(),
          database: projectDatabase,
          limit: 30,
        }
        if (category) params.category = category
        const res = await lcaAPI.searchMethods(params)
        setMethods(res.data?.methods || [])
        setMethodsOpen(true)
        setStatus('idle')
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Method search failed'))
        setStatus('idle')
      }
    },
    [projectDatabase],
  )

  const handleSearchMethods = () => doSearch(methodQuery, selectedCategory)

  const handleSelectCategory = (cat: string) => {
    const next = selectedCategory === cat ? null : cat
    setSelectedCategory(next)
    // Auto-search with the category if we have keywords, otherwise search with category name
    const kw = methodQuery.trim() || cat
    if (next) {
      setMethodQuery(kw === cat ? cat : kw)
      doSearch(kw, next)
    } else if (methodQuery.trim()) {
      doSearch(methodQuery, null)
    }
  }

  const handleSelectMethod = (method: MethodInfo) => {
    setSelectedMethod(method)
    setMethodsOpen(false) // Auto-collapse
    setStatus('ready')
    setResult(null)
    setErrorMsg(null)
  }

  const pollForResult = useCallback(async (taskId: string, attempt: number = 0) => {
    if (attempt > 60) {
      setStatus('error')
      setErrorMsg('Calculation timed out after 5 minutes')
      return
    }
    try {
      const res = await lcaAPI.getTaskStatus(taskId)
      if (res.data.status === 'SUCCESS' && res.data.result) {
        setResult(res.data.result as LCAResult)
        setStatus('done')
        toast.success('Calculation complete')
      }
    } catch (err) {
      const detail = (err as TaskPendingError).response?.data?.detail
      if (
        detail?.details?.status === 'PENDING' ||
        detail?.details?.status === 'STARTED' ||
        detail?.details?.status === 'PROGRESS'
      ) {
        pollRef.current = setTimeout(() => pollForResult(taskId, attempt + 1), 2000)
        return
      }
      setStatus('error')
      const errMsg = typeof detail?.message === 'string' ? detail.message : 'Calculation failed'
      setErrorMsg(errMsg)
    }
  }, [])

  const handleCalculate = async () => {
    if (!selectedMethod || !activity || !projectName) return
    setStatus('submitting')
    setErrorMsg(null)
    setResult(null)
    try {
      const res = await lcaAPI.calculateCustom({
        project_name: projectName,
        activity_code: activity.code,
        quantity: 1,
        method_specification: selectedMethod.method_specification,
      })
      const taskId = res.data.task_id
      if (!taskId) {
        setStatus('error')
        setErrorMsg('No task ID returned')
        return
      }
      setStatus('polling')
      pollRef.current = setTimeout(() => pollForResult(taskId, 0), 2500)
    } catch (err) {
      setStatus('error')
      setErrorMsg(getApiErrorMessage(err, 'Failed to start calculation'))
    }
  }

  const handleExport = () => {
    if (!result) return
    downloadJSON(result, `lca-result-${activity?.code || 'unknown'}.json`)
  }

  if (!activity) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Calculator className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">Select an activity to calculate impacts</p>
      </div>
    )
  }
  if (!projectName || !projectDatabase) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Calculator className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">Select a project first</p>
      </div>
    )
  }

  const isWorking = status === 'submitting' || status === 'polling'

  return (
    <div className="divide-y divide-border">
      {/* Header */}
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold">Impact Assessment</h4>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{activity.name}</p>
          </div>
          {result && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleCalculate}
              disabled={isWorking}
              title="Recalculate (uses credits)"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isWorking && 'animate-spin')} />
            </Button>
          )}
        </div>
      </div>

      {/* Method Selection */}
      <div className="p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Impact Assessment Method</p>

        {/* Popular category quick filters */}
        <div className="flex flex-wrap gap-1">
          {POPULAR_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => handleSelectCategory(cat)}
              disabled={isWorking}
              className={cn(
                'px-2 py-0.5 rounded-full text-[10px] border transition-colors',
                selectedCategory === cat
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/50 border-border hover:bg-accent text-muted-foreground hover:text-foreground',
              )}
            >
              {cat.replace(/^ReCiPe 2016 v1\.03, /, 'ReCiPe ')}
            </button>
          ))}
          {/* Show all categories toggle */}
          <button
            onClick={() => setCategoriesOpen(!categoriesOpen)}
            disabled={isWorking}
            className="px-2 py-0.5 rounded-full text-[10px] border border-border hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
          >
            <Filter className="h-2.5 w-2.5" />
            All
          </button>
        </div>

        {/* Full categories list */}
        {categoriesOpen && categories.length > 0 && (
          <div className="max-h-32 overflow-y-auto border border-border rounded p-1.5 space-y-0.5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  handleSelectCategory(cat)
                  setCategoriesOpen(false)
                }}
                className={cn(
                  'w-full text-left px-2 py-0.5 rounded text-[10px] transition-colors',
                  selectedCategory === cat
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'hover:bg-accent text-muted-foreground',
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Active category filter badge */}
        {selectedCategory && (
          <div className="flex items-center gap-1 text-[10px]">
            <span className="text-muted-foreground">Filtered:</span>
            <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
              {selectedCategory}
              <button
                onClick={() => {
                  setSelectedCategory(null)
                }}
                className="hover:text-primary/70"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          </div>
        )}

        {/* Search box */}
        <div className="flex gap-1.5">
          <Input
            placeholder="e.g. climate change, water, ozone..."
            value={methodQuery}
            onChange={(e) => setMethodQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchMethods()}
            className="h-7 text-xs"
            disabled={isWorking}
          />
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={handleSearchMethods}
            disabled={!methodQuery.trim() || status === 'searching' || isWorking}
          >
            {status === 'searching' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Search className="h-3 w-3" />
            )}
          </Button>
        </div>

        {/* Selected method — compact summary */}
        {selectedMethod && (
          <div className="p-2 rounded-md border border-primary bg-primary/5 flex items-start gap-2">
            <CheckCircle className="h-3 w-3 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{selectedMethod.indicator}</p>
              <p className="text-[10px] text-muted-foreground truncate">
                {selectedMethod.category} &gt; {selectedMethod.subcategory} — {selectedMethod.unit}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedMethod(null)
                setMethodsOpen(true)
                setResult(null)
                setStatus('idle')
              }}
              className="text-muted-foreground hover:text-foreground shrink-0"
              title="Change method"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Method results — collapsible */}
        {methods.length > 0 && (
          <div>
            <button
              onClick={() => setMethodsOpen(!methodsOpen)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
            >
              {methodsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {methods.length} method{methods.length !== 1 ? 's' : ''} found
            </button>
            {methodsOpen && (
              <div className="mt-1 space-y-1 max-h-64 overflow-y-auto">
                {methods.map((m) => (
                  <button
                    key={m.method_specification}
                    onClick={() => handleSelectMethod(m)}
                    className={cn(
                      'w-full text-left p-1.5 rounded border text-xs transition-colors',
                      selectedMethod?.method_specification === m.method_specification
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-accent/50',
                    )}
                  >
                    <span className="font-medium">{m.indicator}</span>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {m.category} &gt; {m.subcategory} — {m.unit}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Calculate button */}
      {selectedMethod && !result && (
        <div className="p-3">
          <Button onClick={handleCalculate} disabled={isWorking} className="w-full gap-2">
            {isWorking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {status === 'submitting' ? 'Submitting...' : 'Calculating...'}
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4" />
                Calculate Impact
                <Coins className="h-3.5 w-3.5 text-yellow-500" />
              </>
            )}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center mt-1.5 flex items-center justify-center gap-1">
            <Coins className="h-3 w-3 text-yellow-500" />
            Uses credits
          </p>
          {status === 'polling' && (
            <p className="text-[10px] text-muted-foreground text-center mt-2">
              <Clock className="h-3 w-3 inline mr-1" />
              Waiting for worker...
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {status === 'error' && errorMsg && (
        <div className="p-3">
          <div className="p-2 rounded-md border border-destructive/50 bg-destructive/5 flex items-start gap-2">
            <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
            <p className="text-xs text-destructive">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* Result */}
      {result && result.lca_score && (
        <div className="p-3 space-y-3">
          <div className="p-3 rounded-lg border border-border bg-muted/30">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
              LCA Score
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold font-mono">
                {formatValue(result.lca_score.value)}
              </span>
              <span className="text-sm text-muted-foreground">{result.lca_score.unit}</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 truncate">
              {result.method_specification}
            </p>
          </div>
          <div className="space-y-1">
            {result.functional_unit && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Functional unit</span>
                <span className="font-mono truncate ml-2">
                  {result.functional_unit.quantity} {result.functional_unit.unit} of{' '}
                  {result.functional_unit.activity_name}
                </span>
              </div>
            )}
            {result.database && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Database</span>
                <span className="font-mono">{result.database}</span>
              </div>
            )}
            {result.calculation_time != null && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Calc time</span>
                <span className="font-mono">{result.calculation_time.toFixed(2)}s</span>
              </div>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full gap-1.5 text-xs"
            onClick={handleExport}
          >
            <Download className="h-3 w-3" />
            Export Result (JSON)
          </Button>
        </div>
      )}
    </div>
  )
}

function formatValue(value: number): string {
  if (value === 0) return '0'
  if (Math.abs(value) < 0.001) return value.toExponential(3)
  if (Math.abs(value) < 1) return value.toFixed(4)
  if (Math.abs(value) >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return value.toFixed(3)
}
